import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { PackagePlus, Plus, Trash2, Save, PlusCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const CATEGORIES = ["Chaises", "Tables", "Accessoires", "Autre"];

// Génère un SKU simple depuis modèle + couleur (3+3 lettres, uppercase, sans accents)
const genSku = (modele, couleur) => {
  const clean = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z]/g, "").toUpperCase().slice(0, 3);
  const m = clean(modele);
  const c = clean(couleur);
  if (!m || !c) return "";
  return `${c}-${m}`;
};

const Arrivages = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [produits, setProduits] = useState([]);
  const [arrivages, setArrivages] = useState([]);
  const [form, setForm] = useState({
    date_arrivage: new Date().toISOString().slice(0, 10),
    fournisseur: "",
    numero_conteneur: "",
    cout_total: "",
    notes: "",
    lignes: [{ sku: "", quantite: 1, cout_unitaire: 0 }],
  });
  const [saving, setSaving] = useState(false);
  // Dialog "Nouveau produit"
  const [newProdOpen, setNewProdOpen] = useState(false);
  const [newProdLigneIdx, setNewProdLigneIdx] = useState(null);
  const [newProdForm, setNewProdForm] = useState({ modele: "", couleur: "", categorie: "Chaises", sku: "", prix_b2c_ttc: 0, prix_b2b_ht: 0, cost_unit: 0 });
  const [creatingProd, setCreatingProd] = useState(false);

  const fetchAll = async () => {
    try {
      const [p, a] = await Promise.all([
        axios.get(`${API}/produits`, { headers }),
        axios.get(`${API}/arrivages`, { headers }),
      ]);
      setProduits(p.data);
      setArrivages(a.data);
    } catch { toast.error("Erreur chargement"); }
  };
  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setLigne = (idx, field, value) => {
    const lignes = [...form.lignes];
    lignes[idx] = { ...lignes[idx], [field]: value };
    setForm({ ...form, lignes });
  };
  const addLigne = () => setForm({ ...form, lignes: [...form.lignes, { sku: "", quantite: 1, cout_unitaire: 0 }] });
  const removeLigne = (idx) => setForm({ ...form, lignes: form.lignes.filter((_, i) => i !== idx) });

  const totalLignes = form.lignes.reduce((s, l) => s + (Number(l.quantite) || 0), 0);
  const coutCalcule = form.lignes.reduce((s, l) => s + ((Number(l.quantite) || 0) * (Number(l.cout_unitaire) || 0)), 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date_arrivage) { toast.error("Date obligatoire"); return; }
    const validLignes = form.lignes.filter(l => l.sku && Number(l.quantite) > 0);
    if (validLignes.length === 0) { toast.error("Ajoute au moins une ligne valide"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/arrivages`, {
        ...form,
        cout_total: parseFloat(form.cout_total) || coutCalcule,
        lignes: validLignes.map(l => ({ sku: l.sku, quantite: parseInt(l.quantite) || 0, cout_unitaire: parseFloat(l.cout_unitaire) || 0 })),
      }, { headers });
      const msg = `Arrivage enregistré ✅ · Stock mis à jour`;
      const bonus = res.data.precommandes_reveillees > 0 ? ` · 🔔 ${res.data.precommandes_reveillees} précommande(s) à contacter !` : "";
      toast.success(msg + bonus, { duration: 8000 });
      setForm({
        date_arrivage: new Date().toISOString().slice(0, 10),
        fournisseur: "",
        numero_conteneur: "",
        cout_total: "",
        notes: "",
        lignes: [{ sku: "", quantite: 1, cout_unitaire: 0 }],
      });
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Annuler cet arrivage ? Le stock ajouté sera retiré.")) return;
    try { await axios.delete(`${API}/arrivages/${id}`, { headers }); toast.success("Annulé"); fetchAll(); }
    catch { toast.error("Erreur"); }
  };

  const openNewProd = (idx) => {
    setNewProdLigneIdx(idx);
    setNewProdForm({ modele: "", couleur: "", categorie: "Chaises", sku: "", prix_b2c_ttc: 0, prix_b2b_ht: 0, cost_unit: 0 });
    setNewProdOpen(true);
  };
  const createProd = async () => {
    if (!newProdForm.modele.trim() || !newProdForm.couleur.trim()) { toast.error("Modèle et couleur obligatoires"); return; }
    const sku = (newProdForm.sku || genSku(newProdForm.modele, newProdForm.couleur)).trim().toUpperCase();
    if (!sku) { toast.error("SKU invalide"); return; }
    if (produits.some(p => p.sku === sku)) { toast.error(`SKU "${sku}" déjà utilisé`); return; }
    setCreatingProd(true);
    try {
      const payload = {
        sku,
        modele: newProdForm.modele.trim(),
        couleur: newProdForm.couleur.trim(),
        categorie: newProdForm.categorie,
        prix_b2c_ttc: parseFloat(newProdForm.prix_b2c_ttc) || 0,
        prix_b2b_ht: parseFloat(newProdForm.prix_b2b_ht) || 0,
        cost_unit: parseFloat(newProdForm.cost_unit) || 0,
        stock_initial: 0,
        seuil_alerte: 10,
      };
      const res = await axios.post(`${API}/produits`, payload, { headers });
      const created = res.data;
      // Rafraîchir la liste des produits
      const p2 = await axios.get(`${API}/produits`, { headers });
      setProduits(p2.data);
      // Renseigner automatiquement la ligne en cours (setForm fonctionnel pour éviter stale closure)
      if (newProdLigneIdx !== null) {
        setForm(prev => {
          const lignes = [...prev.lignes];
          lignes[newProdLigneIdx] = {
            ...lignes[newProdLigneIdx],
            sku: created.sku,
            cout_unitaire: payload.cost_unit || lignes[newProdLigneIdx].cout_unitaire,
          };
          return { ...prev, lignes };
        });
      }
      toast.success(`Produit "${created.modele} ${created.couleur}" créé ✅`);
      setNewProdOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur création produit");
    } finally { setCreatingProd(false); }
  };

  return (
    <div className="space-y-8" data-testid="arrivages-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-2">
          <PackagePlus className="w-7 h-7 text-emerald-600" /> Arrivages
        </h1>
        <p className="text-slate-500 mt-1">Enregistre les livraisons fournisseur — le stock et le coût moyen sont mis à jour automatiquement.</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg">Nouvel arrivage</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2"><Label>Date arrivage *</Label><Input type="date" value={form.date_arrivage} onChange={(e) => setForm({ ...form, date_arrivage: e.target.value })} data-testid="arrivage-date" /></div>
              <div className="space-y-2"><Label>Fournisseur</Label><Input value={form.fournisseur} onChange={(e) => setForm({ ...form, fournisseur: e.target.value })} placeholder="Ex: Furniture Co Ltd" data-testid="arrivage-fournisseur" /></div>
              <div className="space-y-2"><Label>N° conteneur / BL</Label><Input value={form.numero_conteneur} onChange={(e) => setForm({ ...form, numero_conteneur: e.target.value })} placeholder="Ex: MSCU1234567" data-testid="arrivage-conteneur" /></div>
              <div className="space-y-2"><Label>Coût total (€) — facultatif</Label><Input type="number" step="0.01" value={form.cout_total} onChange={(e) => setForm({ ...form, cout_total: e.target.value })} placeholder={coutCalcule ? coutCalcule.toFixed(2) : "0"} data-testid="arrivage-cout-total" /></div>
            </div>

            <div>
              <Label className="mb-2 block">Contenu de l'arrivage</Label>
              <div className="space-y-2">
                {form.lignes.map((l, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-6">
                      <div className="flex gap-1">
                        <Select value={l.sku} onValueChange={(v) => { if (v) setLigne(idx, "sku", v); }}>
                          <SelectTrigger data-testid={`arrivage-sku-${idx}`}><SelectValue placeholder="— Choisir un produit —" /></SelectTrigger>
                          <SelectContent>
                            {produits.map(p => (
                              <SelectItem key={p.sku} value={p.sku}>{p.modele} {p.couleur} <span className="text-xs text-slate-400 ml-1">({p.categorie || "?"} · stock {p.stock_restant})</span></SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button type="button" variant="outline" size="sm" onClick={() => openNewProd(idx)} title="Créer un nouveau produit" data-testid={`arrivage-new-prod-${idx}`}>
                          <PlusCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="col-span-2">
                      <Input type="number" min="1" value={l.quantite} onChange={(e) => setLigne(idx, "quantite", e.target.value)} placeholder="Qté" data-testid={`arrivage-qte-${idx}`} />
                    </div>
                    <div className="col-span-3">
                      <Input type="number" step="0.01" min="0" value={l.cout_unitaire} onChange={(e) => setLigne(idx, "cout_unitaire", e.target.value)} placeholder="Coût unitaire €" data-testid={`arrivage-cout-${idx}`} />
                    </div>
                    <div className="col-span-1">
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeLigne(idx)} disabled={form.lignes.length === 1} className="text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <Button type="button" variant="outline" size="sm" onClick={addLigne} className="mt-3" data-testid="arrivage-add-ligne">
                <Plus className="w-4 h-4 mr-1" />Ajouter une ligne
              </Button>
            </div>

            <div className="p-3 bg-emerald-50 rounded-lg text-sm flex justify-between items-center">
              <span className="text-emerald-800"><b>{totalLignes}</b> unités au total</span>
              {coutCalcule > 0 && <span className="text-emerald-700">Coût calculé : <b>{coutCalcule.toFixed(2)} €</b></span>}
            </div>

            <div className="space-y-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Remarques sur cet arrivage…" rows={2} /></div>

            <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="arrivage-submit">
              <Save className="w-4 h-4 mr-2" />{saving ? "Enregistrement…" : "Enregistrer & mettre à jour le stock"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">Historique des arrivages ({arrivages.length})</CardTitle></CardHeader>
        <CardContent>
          {arrivages.length === 0 ? (
            <p className="text-slate-400 text-sm">Aucun arrivage enregistré.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead><TableHead>Fournisseur</TableHead><TableHead>Conteneur</TableHead><TableHead>Contenu</TableHead><TableHead className="text-right">Coût total</TableHead><TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {arrivages.map((a) => (
                  <TableRow key={a.id} data-testid={`arrivage-row-${a.id}`}>
                    <TableCell className="font-medium">{a.date_arrivage}</TableCell>
                    <TableCell>{a.fournisseur || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-500">{a.numero_conteneur || "—"}</TableCell>
                    <TableCell className="text-sm">
                      {(a.lignes || []).slice(0, 3).map((l, i) => (
                        <div key={i}>{l.quantite}× {l.sku}</div>
                      ))}
                      {(a.lignes || []).length > 3 && <div className="text-xs text-slate-400">+ {a.lignes.length - 3} autres</div>}
                    </TableCell>
                    <TableCell className="text-right">{a.cout_total ? `${a.cout_total.toFixed(2)} €` : "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(a.id)} data-testid={`arrivage-delete-${a.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog Nouveau produit */}
      <Dialog open={newProdOpen} onOpenChange={setNewProdOpen}>
        <DialogContent className="max-w-lg" data-testid="new-prod-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PlusCircle className="w-5 h-5 text-emerald-600" /> Créer un nouveau produit
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="text-xs bg-blue-50 border border-blue-200 rounded-lg p-2 text-blue-900">
              Le produit sera ajouté à ton catalogue puis disponible immédiatement dans l'arrivage.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Modèle *</Label>
                <Input value={newProdForm.modele} onChange={(e) => setNewProdForm({ ...newProdForm, modele: e.target.value, sku: genSku(e.target.value, newProdForm.couleur) })} placeholder="Osaka, Kyoto…" data-testid="new-prod-modele" />
              </div>
              <div>
                <Label>Couleur *</Label>
                <Input value={newProdForm.couleur} onChange={(e) => setNewProdForm({ ...newProdForm, couleur: e.target.value, sku: genSku(newProdForm.modele, e.target.value) })} placeholder="Marron, Vert…" data-testid="new-prod-couleur" />
              </div>
            </div>
            <div>
              <Label>Catégorie *</Label>
              <Select value={newProdForm.categorie} onValueChange={(v) => setNewProdForm({ ...newProdForm, categorie: v })}>
                <SelectTrigger data-testid="new-prod-categorie"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>SKU auto-généré <span className="text-xs text-slate-400">(éditable)</span></Label>
              <Input value={newProdForm.sku} onChange={(e) => setNewProdForm({ ...newProdForm, sku: e.target.value.toUpperCase() })} placeholder="OSA-BRN" className="font-mono" data-testid="new-prod-sku" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Prix B2C TTC</Label>
                <Input type="number" step="0.01" value={newProdForm.prix_b2c_ttc} onChange={(e) => setNewProdForm({ ...newProdForm, prix_b2c_ttc: e.target.value })} data-testid="new-prod-b2c" />
              </div>
              <div>
                <Label className="text-xs">Prix B2B HT</Label>
                <Input type="number" step="0.01" value={newProdForm.prix_b2b_ht} onChange={(e) => setNewProdForm({ ...newProdForm, prix_b2b_ht: e.target.value })} data-testid="new-prod-b2b" />
              </div>
              <div>
                <Label className="text-xs">Coût unit.</Label>
                <Input type="number" step="0.01" value={newProdForm.cost_unit} onChange={(e) => setNewProdForm({ ...newProdForm, cost_unit: e.target.value })} data-testid="new-prod-cost" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewProdOpen(false)} disabled={creatingProd}>Annuler</Button>
            <Button onClick={createProd} disabled={creatingProd} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-prod-submit">
              {creatingProd ? "Création…" : <><PlusCircle className="w-4 h-4 mr-2" />Créer</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Arrivages;
